import logger from '@eneco/common/log';

import { PriceSimulationFormSchema } from '../schemas/priceSimulationFormSchema';
import { PriceSimulationStep } from '../types/PriceSimulationSteps';

export const LOCAL_STORAGE_PRICE_SIMULATION_STATE = 'priceSimulationState';
const LOCAL_STORAGE_EXPIRY_DAYS = 7;

export interface PriceSimulationLocalStorageData {
  formValues: Partial<PriceSimulationFormSchema>;
  timestamp: number;
  expires: number;
  priceSimulationStep: PriceSimulationStep;
}

/**
 * Save the current step and form values to a LocalStorage
 */
export const savePriceSimulationState = ({
  formValues,
  priceSimulationStep,
}: {
  formValues?: Partial<PriceSimulationFormSchema>;
  priceSimulationStep?: PriceSimulationStep | null;
}): void => {
  try {
    const LocalStorageValue = localStorage.getItem(LOCAL_STORAGE_PRICE_SIMULATION_STATE);
    const data: PriceSimulationLocalStorageData = LocalStorageValue
      ? JSON.parse(LocalStorageValue)
      : {
          formValues: {},
          timestamp: Date.now(),
          priceSimulationStep: null,
        };

    if (formValues) {
      data.formValues = formValues;
    }

    if (priceSimulationStep) {
      data.priceSimulationStep = priceSimulationStep;
    }

    localStorage.setItem(
      LOCAL_STORAGE_PRICE_SIMULATION_STATE,
      JSON.stringify({ ...data, expires: LOCAL_STORAGE_EXPIRY_DAYS } as PriceSimulationLocalStorageData),
    );
  } catch (error) {
    logger.error(
      'Failed to save price simulation state to local:',
      error instanceof Error ? error.message : String(error),
    );
  }
};

/**
 * Load the step and form values from the local storage
 */
export const loadPriceSimulationState = (): PriceSimulationLocalStorageData | null => {
  try {
    const LocalStorageValue = localStorage.getItem(LOCAL_STORAGE_PRICE_SIMULATION_STATE);
    if (!LocalStorageValue) {
      return null;
    }

    const data: PriceSimulationLocalStorageData = JSON.parse(LocalStorageValue);

    const expiryInMs = LOCAL_STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    if (Date.now() - data.timestamp > expiryInMs) {
      clearPriceSimulationState();
      return null;
    }

    return data;
  } catch (error) {
    logger.error(
      'Failed to load price simulation state from local storage:',
      error instanceof Error ? error.message : String(error),
    );
    return null;
  }
};

/**
 * Clear the price simulation state from local storage
 */
export const clearPriceSimulationState = (): void => {
  try {
    localStorage.removeItem(LOCAL_STORAGE_PRICE_SIMULATION_STATE);
  } catch (error) {
    logger.error(
      'Failed to clear price simulation state from local storage:',
      error instanceof Error ? error.message : String(error),
    );
  }
};
